import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '@/shared/ui/icon';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/shadcn/popover';

import { FilterValueEditor } from '../editors/FilterValueEditor';
import { chipSummary, type ChipSummaryTexts } from '../model/filterModel';
import type { ListEnumFilterDef, ListFilterFieldDef, ListFilterValue } from '../model/types';
import type { ListPageController } from '../model/useListPageController';

function useChipSummaryTexts(): ChipSummaryTexts {
  const { t } = useTranslation('listPage');
  return useMemo(
    () => ({
      all: t('chips.all'),
      from: (value: string) => t('chips.from', { value }),
      to: (value: string) => t('chips.to', { value }),
      selected: (count: number) => t('chips.selected', { count }),
    }),
    [t]
  );
}

/** Souhrn enum chipu — popisky vybraných id se dohledávají z options (vlastní komponenta kvůli hooku). */
function EnumChipSummary({ def, value }: { def: ListEnumFilterDef; value: ListFilterValue }) {
  const texts = useChipSummaryTexts();
  const { options } = def.options.useOptions();
  const labelMap = useMemo(
    () => new Map(options.map((option) => [option.id, option.label])),
    [options]
  );
  return <>{chipSummary(def, value, texts, labelMap)}</>;
}

function FilterChip<TItem>({
  def,
  value,
  controller,
}: {
  def: ListFilterFieldDef;
  value: ListFilterValue;
  controller: ListPageController<TItem>;
}) {
  const { t } = useTranslation('listPage');
  const texts = useChipSummaryTexts();

  const summary =
    def.type === 'enum' ? (
      <EnumChipSummary def={def} value={value} />
    ) : (
      chipSummary(def, value, texts)
    );

  return (
    <Popover>
      <span className="phxlist-chip" data-pinned={value.pinned || undefined}>
        <button
          type="button"
          className="phxlist-chip-pin"
          data-on={value.pinned || undefined}
          title={value.pinned ? t('chips.unpin') : t('chips.pin')}
          onClick={() => controller.togglePin(def.key)}
        >
          <Icon name="pin" size={11} />
        </button>
        <PopoverTrigger asChild>
          <button type="button" className="phxlist-chip-main">
            <span className="phxlist-chip-key">{def.label}</span>
            <span className="phxlist-chip-val">{summary}</span>
            <span className="phxlist-chip-caret">
              <Icon name="chevdown" size={12} />
            </span>
          </button>
        </PopoverTrigger>
        <button
          type="button"
          className="phxlist-chip-x"
          title={value.pinned ? t('chips.clearPinned') : t('chips.remove')}
          onClick={() => controller.clearFilter(def.key)}
        >
          <Icon name="x" size={12} />
        </button>
      </span>
      <PopoverContent align="start" sideOffset={5} className="w-auto p-0">
        <FilterValueEditor
          def={def}
          value={value}
          onChange={(next) => controller.upsertFilter({ ...next, pinned: value.pinned })}
        />
      </PopoverContent>
    </Popover>
  );
}

/**
 * Řádek chipů (anatomie 01 §3) — přepínač panelu filtrů + chip pro každý aktivní/připnutý
 * filtr + hromadné Vymazat. Chipy i panel editují tentýž stav filtrů v controlleru.
 */
export function ChipsRow<TItem>({ controller }: { controller: ListPageController<TItem> }) {
  const { t } = useTranslation('listPage');
  const filters = controller.filters;

  return (
    <div className="phxlist-chiprow">
      <button
        type="button"
        className="phxlist-btn"
        data-active={controller.panelOpen || undefined}
        onClick={() => controller.setPanelOpen(!controller.panelOpen)}
      >
        <Icon name="filter" size={13} />
        {t('chips.filters')}
        {filters.length > 0 ? ` (${filters.length})` : ''}
      </button>
      <span className="phxlist-vdivider" />
      {filters.length === 0 ? (
        <span className="text-phx-sm text-phx-text-faint">{t('chips.empty')}</span>
      ) : null}
      {filters.map((value) => {
        const def = controller.fieldDef(value.field);
        if (!def) {
          return null;
        }
        return <FilterChip key={value.field} def={def} value={value} controller={controller} />;
      })}
      {filters.length > 0 ? (
        <button
          type="button"
          className="phxlist-btn"
          data-variant="ghost"
          onClick={controller.clearAllFilters}
        >
          <Icon name="x" size={12} />
          {t('chips.clearAll')}
        </button>
      ) : null}
    </div>
  );
}
